<template>
  <div class="user-management">
    <div class="page-title">人员管理</div>
    
    <!-- 搜索筛选区域 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-input
          placeholder="搜索部门/姓名"
          prefix-icon="el-icon-search"
          v-model="searchKeyword"
          clearable
          @keyup.enter.native="handleSearch"
          class="filter-input">
        </el-input>
        <el-button type="primary" plain @click="handleSearch">搜索</el-button>
        <el-button @click="resetSearch">重置</el-button>
        <div class="spacer"></div>
        <el-button type="primary" icon="el-icon-refresh" @click="handleSync">手动同步</el-button>
      </div>
    </div>

    <el-table
      :data="userData"
      border
      style="width: 100%"
      v-loading="loading">
      <el-table-column prop="workNumber" label="人员编号" width="100" align="center"></el-table-column>
      <el-table-column prop="name" label="姓名" width="120"></el-table-column>
      <el-table-column prop="username" label="账号" width="120"></el-table-column>
      <el-table-column prop="department" label="部门" width="150"></el-table-column>
      <el-table-column prop="role" label="角色" width="120"></el-table-column>
      <el-table-column prop="healthStatus" label="健康评级" width="120" align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.healthStatus }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="createBy" label="创建人" width="120"></el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="160"></el-table-column>
      <el-table-column prop="lastModifier" label="最后修改人" width="120"></el-table-column>
      <el-table-column prop="lastModifiedTime" label="最后修改时间" width="160"></el-table-column>
      <el-table-column label="操作" min-width="250" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleResetPassword(scope.row)">重置密码</el-button>
          <el-button type="text" @click="handleModifyRole(scope.row)">修改角色</el-button>
          <el-button type="text" @click="handleModifyHealthRating(scope.row)">修改健康评级</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 修改角色弹窗 -->
    <el-dialog
      title="修改角色"
      :visible.sync="roleDialogVisible"
      width="400px"
      :close-on-click-modal="false">
      
      <div class="dialog-content" v-loading="roleLoading">
        <div class="form-item">
          <div class="form-label">姓名</div>
          <div class="form-value">{{ currentUser.name }}</div>
        </div>
        
        <div class="form-item">
          <div class="form-label">部门</div>
          <div class="form-value">{{ currentUser.department }}</div>
        </div>
        
        <div class="form-item">
          <div class="form-label">角色</div>
          <el-select v-model="currentUser.roleId" placeholder="请选择角色" class="form-input">
            <el-option 
              v-for="item in roleList" 
              :key="item.id" 
              :label="item.name" 
              :value="item.id">
            </el-option>
          </el-select>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="roleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRoleChange" :loading="loading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 修改健康评级弹窗 -->
    <el-dialog
      title="修改健康评级"
      :visible.sync="healthRatingDialogVisible"
      width="400px"
      :close-on-click-modal="false">
      
      <div class="dialog-content" v-loading="healthRatingLoading">
        <div class="form-item">
          <div class="form-label">姓名</div>
          <div class="form-value">{{ currentUser.name }}</div>
        </div>
        
        <div class="form-item">
          <div class="form-label">部门</div>
          <div class="form-value">{{ currentUser.department }}</div>
        </div>
        
        <div class="form-item">
          <div class="form-label">当前健康评级</div>
          <div class="form-value">
            {{ currentUser.healthStatus }}
          </div>
        </div>
        
        <div class="form-item">
          <div class="form-label">健康评级</div>
          <el-select v-model="currentUser.healthRatingId" placeholder="请选择健康评级" class="form-input">
            <el-option 
              v-for="item in healthRatingOptions" 
              :key="item.value" 
              :label="item.label" 
              :value="item.value">
            </el-option>
          </el-select>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="healthRatingDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitHealthRatingChange" :loading="loading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { searchUsers, resetUserPassword, updateUserRole, updateUserHealthRating } from '@/api/user'
import { getAllRoles } from '@/api/role'
import { getAllHealthRatings } from '@/api/health-rating'

export default {
  name: 'UserManagement',
  data() {
    return {
      searchKeyword: '',
      deptKeyword: '',
      userData: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      
      // 修改角色弹窗相关数据
      roleDialogVisible: false,
      roleList: [], // 角色列表
      roleLoading: false, // 加载角色列表的状态
      
      // 修改健康评级弹窗相关数据
      healthRatingDialogVisible: false,
      healthRatingList: [], // 健康评级列表
      healthRatingLoading: false, // 加载健康评级列表的状态
      
      // 当前操作的用户
      currentUser: {
        userId: '',
        name: '',
        department: '',
        role: '',
        roleId: '', // 角色ID字段
        healthStatus: '',
        healthRatingId: '', // 健康评级ID字段
        newHealthStatus: ''
      },
      
      // 健康评级选项 - 改为动态加载
      healthRatingOptions: []
    };
  },
  created() {
    this.fetchUserList();
    this.fetchRoleList(); // 获取角色列表
    this.fetchHealthRatingList(); // 获取健康评级列表
  },
  methods: {
    // 获取用户列表
    fetchUserList() {
      this.loading = true;
      
      // 准备查询参数
      const params = {};
      if (this.searchKeyword && this.searchKeyword.trim() !== '') {
        params.keyword = this.searchKeyword.trim();
      }
      
      // 添加分页参数
      params.pageNum = this.currentPage;
      params.pageSize = this.pageSize;
      
      console.log('发送分页查询用户请求，参数:', JSON.stringify(params));
      
      searchUsers(params)
        .then(response => {
          console.log('分页查询用户响应:', response);
          if (response && response.code === 200) {
            // 检查响应数据结构
            let dataList = [];
            
            if (response.data && Array.isArray(response.data.list)) {
              // 标准分页响应结构
              dataList = response.data.list || [];
              this.total = response.data.total || 0;
            } else if (response.data && Array.isArray(response.data)) {
              // 直接返回数组的结构
              dataList = response.data;
              this.total = dataList.length;
            } else {
              dataList = [];
              this.total = 0;
            }
            
            this.userData = this.processUserData(dataList);
          } else {
            this.$message.error((response && response.msg) || '获取用户列表失败');
            this.userData = [];
            this.total = 0;
          }
        })
        .catch(error => {
          console.error('获取用户列表失败:', error);
          this.$message.error('获取用户列表失败，请刷新页面重试');
          this.userData = [];
          this.total = 0;
        })
        .finally(() => {
        this.loading = false;
        });
    },
    
    // 获取角色列表
    fetchRoleList() {
      this.roleLoading = true;
      getAllRoles()
        .then(response => {
          if (response && response.code === 200 && response.data) {
            this.roleList = Array.isArray(response.data) ? response.data : [];
            console.log('获取角色列表成功:', this.roleList);
          } else {
            this.$message.error((response && response.msg) || '获取角色列表失败');
            this.roleList = [];
          }
        })
        .catch(error => {
          console.error('获取角色列表失败:', error);
          this.$message.error('获取角色列表失败');
          this.roleList = [];
        })
        .finally(() => {
          this.roleLoading = false;
        });
    },
    
    // 获取健康评级列表
    fetchHealthRatingList() {
      this.healthRatingLoading = true;
      getAllHealthRatings()
        .then(response => {
          if (response && response.code === 200 && response.data) {
            this.healthRatingList = Array.isArray(response.data) ? response.data : [];
            // 转换为下拉选项格式
            this.healthRatingOptions = this.healthRatingList.map(item => ({
              value: item.id,
              label: item.name
            }));
            console.log('获取健康评级列表成功:', this.healthRatingList);
          } else {
            this.$message.error((response && response.msg) || '获取健康评级列表失败');
            this.healthRatingList = [];
            this.healthRatingOptions = [];
          }
        })
        .catch(error => {
          console.error('获取健康评级列表失败:', error);
          this.$message.error('获取健康评级列表失败');
          this.healthRatingList = [];
          this.healthRatingOptions = [];
        })
        .finally(() => {
          this.healthRatingLoading = false;
        });
    },
    
    // 处理用户数据
    processUserData(dataList) {
      return dataList.map(item => {
        return {
          userId: item.id || item.userId,
          workNumber: item.workNumber || '-',
          username: item.account || '-',
          name: item.realName || item.name || '-',
          phone: item.phone || '-',
          email: item.email || '-',
          department: item.departmentName || item.department || '-',
          role: item.roleName || item.role || '-',
          roleId: item.roleId || '', // 角色ID
          healthStatus: item.healthRatingName || item.healthStatus || '未评级',
          healthRatingId: item.healthRatingId || '', // 健康评级ID
          status: item.status,
          createBy: item.createBy || item.createUserName || '-',
          createTime: item.createTime ? this.formatDateTime(item.createTime) : '-',
          lastModifier: item.updateUserName || item.lastModifier || '-',
          lastModifiedTime: item.updateTime ? this.formatDateTime(item.updateTime) : (item.lastModifiedTime || '-')
        };
      });
    },
    
    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp; // 如果不是有效日期，则返回原值
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1; // 重置到第一页
      this.fetchUserList();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchKeyword = '';
      this.currentPage = 1;
      this.fetchUserList();
    },
    
    // 重置密码
    handleResetPassword(row) {
      this.$confirm('确认重置该用户的密码吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        resetUserPassword(row.userId)
          .then(response => {
            if (response && response.code === 200) {
        this.$message.success(`已重置 ${row.name} 的密码为初始密码`);
            } else {
              this.$message.error((response && response.msg) || '重置密码失败');
            }
          })
          .catch(error => {
            console.error('重置密码失败:', error);
            this.$message.error('重置密码失败，请稍后重试');
          })
          .finally(() => {
            this.loading = false;
          });
      }).catch(() => {
        // 取消操作
      });
    },
    
    // 打开修改角色弹窗
    handleModifyRole(row) {
      this.currentUser = {
        userId: row.userId,
        name: row.name,
        department: row.department,
        role: row.role,
        roleId: row.roleId,
        healthStatus: row.healthStatus
      };
      
      // 如果没有角色列表，重新获取
      if (this.roleList.length === 0) {
        this.fetchRoleList();
      }
      
      this.roleDialogVisible = true;
    },
    
    // 提交角色修改
    submitRoleChange() {
      if (!this.currentUser.roleId) {
        this.$message.warning('请选择角色');
        return;
      }
      
      this.loading = true;
      
      // 准备提交数据
      const data = {
        userId: this.currentUser.userId,
        roleId: this.currentUser.roleId
      };
      
      updateUserRole(data)
        .then(response => {
          if (response && response.code === 200) {
            // 获取选中的角色名称
            const selectedRole = this.roleList.find(role => role.id === this.currentUser.roleId);
            const roleName = selectedRole ? selectedRole.name : '未知角色';
            
            // 获取当前登录用户的姓名
            const currentUsername = this.$store.getters.name || '系统管理员';
            
            // 更新本地数据
      const index = this.userData.findIndex(item => item.userId === this.currentUser.userId);
      if (index !== -1) {
              this.userData[index].role = roleName;
              this.userData[index].roleId = this.currentUser.roleId;
              this.userData[index].lastModifier = currentUsername;
        this.userData[index].lastModifiedTime = new Date().toLocaleString();
            }
            
            this.$message.success(`已将 ${this.currentUser.name} 的角色修改为 ${roleName}`);
            this.roleDialogVisible = false;
          } else {
            this.$message.error((response && response.msg) || '修改角色失败');
          }
        })
        .catch(error => {
          console.error('修改角色失败:', error);
          this.$message.error('修改角色失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 打开修改健康评级弹窗
    handleModifyHealthRating(row) {
      this.currentUser = {
        userId: row.userId,
        name: row.name,
        department: row.department,
        role: row.role,
        roleId: row.roleId,
        healthStatus: row.healthStatus,
        healthRatingId: row.healthRatingId || '',
        newHealthStatus: row.healthStatus
      };
      
      // 如果没有健康评级列表，重新获取
      if (this.healthRatingList.length === 0) {
        this.fetchHealthRatingList();
      }
      
      this.healthRatingDialogVisible = true;
    },
    
    // 提交健康评级修改
    submitHealthRatingChange() {
      if (!this.currentUser.healthRatingId) {
        this.$message.warning('请选择健康评级');
        return;
      }
      
      this.loading = true;
      
      // 准备提交数据
      const data = {
        userId: this.currentUser.userId,
        healthRatingId: this.currentUser.healthRatingId
      };
      
      updateUserHealthRating(data)
        .then(response => {
          if (response && response.code === 200) {
            // 获取选中的健康评级名称
            const selectedRating = this.healthRatingList.find(item => item.id === this.currentUser.healthRatingId);
            const ratingName = selectedRating ? selectedRating.name : '未知评级';
            
            // 获取当前登录用户的姓名
            const currentUsername = this.$store.getters.name || '系统管理员';
            
            // 更新本地数据
      const index = this.userData.findIndex(item => item.userId === this.currentUser.userId);
      if (index !== -1) {
              this.userData[index].healthStatus = ratingName;
              this.userData[index].healthRatingId = this.currentUser.healthRatingId;
              this.userData[index].lastModifier = currentUsername;
        this.userData[index].lastModifiedTime = new Date().toLocaleString();
            }
            
            this.$message.success(`已将 ${this.currentUser.name} 的健康评级修改为 ${ratingName}`);
            this.healthRatingDialogVisible = false;
          } else {
            this.$message.error((response && response.msg) || '修改健康评级失败');
          }
        })
        .catch(error => {
          console.error('修改健康评级失败:', error);
          this.$message.error('修改健康评级失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 分页相关方法
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchUserList();
    },
    
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchUserList();
    },
    
    // 手动同步
    handleSync() {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
        this.$message.success('同步成功，已更新3条数据');
      }, 1500);
    }
  }
};
</script> 

<style scoped>
.user-management {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

/* 筛选区域样式 */
.filter-section {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
}

.filter-input {
  width: 300px;
}

.spacer {
  flex-grow: 1;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 弹窗样式 */
.dialog-content {
  padding: 0 20px;
}

.form-item {
  margin-bottom: 20px;
}

.form-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;
}

.form-value {
  font-size: 14px;
  color: #303133;
}

.form-input {
  width: 100%;
}
</style> 